import { BoxV2 as Box, ExpandableText, Grid } from 'portal-commons';
import React, { MouseEvent } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import CroppableItem from './CroppableItem';
import { hasBrand } from '@/applications/CspPortal/Brands/apiServices';
import { Event } from '@/applications/CspPortal/Events/types';
import { createCampaignRedirectorPath } from '@/utils/path';
import { humanizeAuthenticatedUser } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';

interface Props {
  event: Event;
  campaignLink?: boolean;
  brandLink?: boolean;
  withoutUser?: boolean;
}

const EventListingRow: React.FC<Props> = ({
  event,
  campaignLink = false,
  brandLink = false,
  withoutUser = false,
}) => {
  const location = useLocation();
  const history = useHistory();
  const createDate = convertTimeWithTimezone(event.createDate);
  const authenticatedUser = humanizeAuthenticatedUser(event.authenticatedUser);

  return (
    <Box as="tr" data-testid="eventListingRow">
      <Box
        as="td"
        sx={{
          textAlign: 'left',
          fontWeight: 500,
        }}
        data-testid="eventListingRowEventType"
      >
        {event.eventType ?? '--'}
      </Box>
      <Box
        as="td"
        sx={{ textAlign: 'left' }}
        data-testid="eventListingRowCategoryType"
      >
        {event.categoryType}
      </Box>
      <Box
        as="td"
        data-testid="eventListingRowCampaignUid"
        onClick={() => {
          if (campaignLink && event.campaignUid) {
            history.push({
              pathname: createCampaignRedirectorPath(event.campaignUid),
              state: {
                goBackPage: `${location.pathname}${
                  location.search ? location.search : ''
                }`,
              },
            });
          }
        }}
        sx={{
          textAlign: 'left',
          ...(campaignLink && event.campaignUid
            ? {
                '&:hover': {
                  color: '#00667D !important',
                  fontWeight: 'bold',
                },
                cursor: 'pointer',
              }
            : {}),
        }}
      >
        {event.campaignUid}
      </Box>
      <Box
        as="td"
        data-testid="eventListingRowBrandUid"
        onClick={async () => {
          if (brandLink && event.brandUid) {
            const brandFound = await hasBrand(event.brandUid);
            if (brandFound) {
              history.push({
                pathname: `/brands/${event.brandUid}`,
                state: {
                  goBackPage: `${location.pathname}${
                    location.search ? location.search : ''
                  }`,
                },
              });
            }
          }
        }}
        sx={{
          textAlign: 'left',
          ...(brandLink && event.brandUid
            ? {
                '&:hover': {
                  color: '#00667D !important',
                  fontWeight: 'bold',
                },
                cursor: 'pointer',
              }
            : {}),
        }}
      >
        {event.brandUid}
      </Box>
      <Box
        as="td"
        sx={{ textAlign: 'left', maxWidth: '160px' }}
        data-testid="eventListingRowCreateDate"
      >
        <CroppableItem title={createDate}>{createDate}</CroppableItem>
      </Box>
      {!withoutUser && (
        <Box
          as="td"
          sx={{ textAlign: 'left', maxWidth: '120px' }}
          data-testid="eventListingRowAuthenticatedUser"
        >
          <CroppableItem title={authenticatedUser}>
            {authenticatedUser}
          </CroppableItem>
        </Box>
      )}
      <Box
        as="td"
        sx={{ textAlign: 'left' }}
        onClick={(e: MouseEvent<HTMLTableCellElement>) => e.stopPropagation()}
        data-testid="eventListingRowDescription"
      >
        <Grid
          sx={{
            gridTemplateColumns: 'minmax(200px, 1fr)',
            width: '100%',
          }}
        >
          <ExpandableText text={event.description} maxWidth="100%" />
        </Grid>
      </Box>
    </Box>
  );
};

export default EventListingRow;
